import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import { ToastContainer, toast } from 'react-toastify';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email
} from '@mui/icons-material';

import Layout from './layout/Layout';
import Copyright from '../components/common/Copyright';
import "react-toastify/dist/ReactToastify.css";
import styles from './AdminPage.module.css';

interface User {
  id: number | string;
  UserID: string;
  email: string;
  username?: string;
  Domain: string;
  requestFor: string;
  modal_name?: string;
  modal_Id?: string;
  status: string;
  date: string;
  rejectReason?: string;
}

const AdminPage: React.FC = () => {
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<User[]>([]);
  const [rejectedUsers, setRejectedUsers] = useState<User[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected' | 'implemented'>('pending');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalReason, setModalReason] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionRequests();
  }, []);

  const fetchSubscriptionRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const Email = localStorage.getItem('Email');
      const response = await fetch('/get_subscription_requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Email }),
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();

      const pending: User[] = [];
      const approved: User[] = [];
      const rejected: User[] = [];

      data.forEach((user: any) => {
        const baseUser: User = {
          id: user.SrNo || user.ID,
          UserID: user.UserID || user.UserId,
          email: user.EmailID || '',
          Domain: user.Domain || '',
          requestFor: user.ModelDisplayName || user.ModelName,
          modal_name: `Model ${user.ModelRequestedFor}` || '',
          modal_Id: user.ModelRequestedFor?.toString() || '',
          status:
            user.Status === 'P'
              ? 'Pending'
              : user.Status === 'A'
              ? 'Approved'
              : 'Rejected',
          date: new Date(user.RequestDateTime || user.SubscriptionStart).toLocaleString(),
          rejectReason: user.Status === 'R' ? user.RejectReason || 'N/A' : undefined,
        };

        if (user.Status === 'P') pending.push(baseUser);
        else if (user.Status === 'A') approved.push(baseUser);
        else if (user.Status === 'R') rejected.push(baseUser);
      });

      setPendingUsers(pending);
      setApprovedUsers(approved);
      setRejectedUsers(rejected);
    } catch (err: any) {
      setError(err.message || 'Unable to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (user: User) => {
    try {
     // const Email = localStorage.getItem('Email');
      const response = await fetch('/subscribe_model_to_user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          ModelID: user.modal_Id
        }),
      });

      if (!response.ok) throw new Error('Failed to approve user.');

      toast.success(`Subscribed successfully`);
      setApprovedUsers(prev => [...prev, { ...user, status: 'Approved' }]);
      setPendingUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (error) {
      toast.error('Failed to approve user.');
    }
  };

  const handleRejectWithReason = async (user: User, reason: string) => {
    try {

      const response = await fetch('/Reject_Subscription_request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
         email: user.email,
          ModelID: user.modal_Id,
          RejectReason: reason,
        }),
      });

      if (!response.ok) throw new Error('Failed to reject user.');

      toast.success('User rejected successfully.');
      setRejectedUsers(prev => [...prev, { ...user, status: 'Rejected', rejectReason: reason }]);
      setPendingUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (error) {
      toast.error('Failed to reject user.');
    }
  };

  const renderTable = (title: string, users: User[]) => (
    <>
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="modal-content" style={{ width: 400, maxWidth: '90%' }}>
          <button onClick={() => setIsModalOpen(false)} className="close-btn">X</button>
          <h3 className="modal-title mb-2">Reject User</h3>
          <p>Please provide a reason for rejection:</p>
          <textarea
            rows={5}
            placeholder="Enter reason here..."
            value={modalReason}
            onChange={(e) => setModalReason(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
          <div style={{ marginTop: "16px", textAlign: "right" }}>
            <button className={styles.cancelBtnDeleteChat} onClick={() => setIsModalOpen(false)}>Cancel</button>
            <button
              className={styles.submitBtnChat}
              disabled={!modalReason.trim()}
              onClick={() => {
                if (selectedUser) {
                  handleRejectWithReason(selectedUser, modalReason);
                  setIsModalOpen(false);
                }
              }}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>

      <div className="tableWrapperOuter">
        <div className="tableWrapper">
          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Email Id</th>
                <th>Request For</th>
                <th>Status</th>
                <th>Date</th>
                {title === 'Rejected Users' && <th>Reject Reason</th>}
                {title === 'Pending Users' && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} align="center">Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={7} align="center" style={{ color: 'red' }}>{error}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} align="center">No records found.</td></tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id}>
                    <td>{index + 1}</td>
                    <td>{user.email}</td>
                    <td>{user.requestFor}</td>
                    {/* <td><span className={{ backgroundColor: user.status === 'Approved' ? '#daffda' : user.status === 'Rejected' ? '#ffd7d8' : '#000' }}>{user.status}</span>     </td> */}
                    <td><span className={`${user.status === 'Approved' ? 'approved_status' : user.status === 'Rejected' ? 'reject_status': '#000'  }`}>{user.status}</span>     </td>
                    <td>{user.date}</td>
                    {title === 'Rejected Users' && <td>{user.rejectReason}</td>}
                    {title === 'Pending Users' && (
                      <td>
                        <div className='admin-actionwrapper'>
                          <button className='approveBtn' onClick={() => handleApprove(user)}>Approve</button>
                          <button className='rejectBtn' onClick={() => {
                            setSelectedUser(user);
                            setModalReason('');
                            setIsModalOpen(true);
                          }}>Reject</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="admin-wrapper">
        <div className="admin-body-container">
          <div className="page-wrapper">
            <div className="tabWrapper">
              <button
                className={activeTab === 'pending' ? 'adminTabButton active' : 'adminTabButton'}
                onClick={() => setActiveTab('pending')}
              >
                Pending Request ({pendingUsers.length})
              </button>
              <button
                className={activeTab === 'approved' ? 'adminTabButton active' : 'adminTabButton'}
                onClick={() => setActiveTab('approved')}
              >
                Approved Request ({approvedUsers.length})
              </button>
              <button
                className={activeTab === 'implemented' ? 'adminTabButton active' : 'adminTabButton'}
                onClick={() => setActiveTab('implemented')}
              >
                Implementation Status ({rejectedUsers.length})
              </button>
              <button
                className={activeTab === 'rejected' ? 'adminTabButton active' : 'adminTabButton'}
                onClick={() => setActiveTab('rejected')}
              >
                Rejected Request ({rejectedUsers.length})
              </button>
            </div>

            {activeTab === 'pending' && renderTable('Pending Users', pendingUsers)}
            {activeTab === 'approved' && renderTable('Approved Users', approvedUsers)}
            {activeTab === 'rejected' && renderTable('Rejected Users', rejectedUsers)}
            {activeTab === 'implemented' && renderTable('Rejected Users', rejectedUsers)}
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default AdminPage;
